import {
  Component,
  inject,
  input,
  OnInit,
  Optional,
  output,
  SkipSelf,
  contentChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  FormControlName,
  FormGroup,
  FormGroupDirective,
  FormGroupName,
  ReactiveFormsModule,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {InputTextModule} from 'primeng/inputtext';
import {InputNumberModule} from 'primeng/inputnumber';
import {PasswordModule} from 'primeng/password';
import {MessageModule} from 'primeng/message';
import {TranslocoPipe} from '@jsverse/transloco';
import {Divider} from 'primeng/divider';
import {DatePicker} from 'primeng/datepicker';
import {Select} from 'primeng/select';
import {SelectButtonModule} from 'primeng/selectbutton';
import {DirectusTooltipComponent} from '../../directus-tooltip/directus-tooltip.component';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {DirectusTranslatedItem} from '@frontend/directus';
import {Textarea} from 'primeng/textarea';
import {FileSelectEvent, FileUpload} from 'primeng/fileupload';
import {SelectComponent} from '../select/select.component';
import {SelectTranslationOption} from '../select/select.types';

@Component({
  selector: 'app-form-field',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    PasswordModule,
    MessageModule,
    TranslocoPipe,
    Divider,
    DatePicker,
    Select,
    SelectButtonModule,
    DirectusTooltipComponent,
    TranslateDirectusPipe,
    Textarea,
    FileUpload,
    SelectComponent,
  ],
  templateUrl: 'form-field.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (parentGroupName: FormGroupName, parentFormDir: FormGroupDirective) =>
        parentGroupName ?? parentFormDir,
      deps: [
        [new Optional(), new SkipSelf(), FormGroupName],
        [new Optional(), new SkipSelf(), FormGroupDirective],
      ],
    },
  ],
  styleUrl: 'form-field.component.scss',
})
export class FormFieldComponent implements OnInit {
  readonly id = input.required<string>();
  readonly fieldName = input.required<string>();
  readonly label = input('');
  readonly type = input<
    | 'text'
    | 'email'
    | 'password'
    | 'passwordRepeat'
    | 'number'
    | 'tel'
    | 'datePicker'
    | 'dropdownSelect'
    | 'dropdownSelectV2'
    | 'selectButton'
    | 'textarea'
    | 'file'
  >('text');
  readonly placeholder = input<string>();
  readonly accept = input<string>();
  readonly customErrorKey = input<string>();
  readonly disabledSignal = input<() => boolean>();
  readonly smallLabel = input(false);
  readonly showButtons = input(true);
  readonly showError = input(true);
  readonly mode = input<'decimal' | 'integer'>('decimal');
  readonly step = input(1);
  readonly maxFractionDigits = input(2);
  readonly dropDownOptions = input<
    {
      label: string;
      value: any;
    }[]
  >();
  readonly selectTranslatedOptions = input<DirectusTranslatedItem[]>();
  @Optional() private readonly controlContainer = inject(ControlContainer);
  readonly formControlName = contentChild(FormControlName);

  editable = input<boolean>(true);

  // app-select specific logic
  dropDownOptionsV2 = input<SelectTranslationOption[]>([]);
  selectedOptionId = input<string | null>(null);
  relationshipIdChange = output<string | null>();

  readonly valueAsDisabled = input<string>();

  protected valueAsDisabledComputed(): string {
    const valueAsDisabled = this.valueAsDisabled();
    if (valueAsDisabled) {
      return valueAsDisabled;
    }
    return this.form?.get(this.fieldName())?.value;
  }
  strongRegex = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})';

  public effectiveMin?: number;
  public effectiveMax?: number;

  private static readonly MIN_SENTINEL = Number.NEGATIVE_INFINITY;
  private static readonly MAX_SENTINEL = Number.POSITIVE_INFINITY;

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  get isRequired(): boolean {
    const control = this.form?.get(this.fieldName());
    if (!control || !control.validator) {
      return false;
    }

    const validator = control.validator({value: ''} as AbstractControl);
    return !!validator?.['required'];
  }

  ngOnInit() {
    const ctrl = this.form?.get(this.fieldName());
    this.effectiveMin = this.extractThreshold(ctrl, 'min', FormFieldComponent.MIN_SENTINEL);
    this.effectiveMax = this.extractThreshold(ctrl, 'max', FormFieldComponent.MAX_SENTINEL);

    this.effectiveMin ??= 0;
  }

  private extractThreshold(
    control: AbstractControl | null,
    type: 'min' | 'max',
    sentinel: number,
  ): number | undefined {
    const validatorFn = control?.validator;
    if (!validatorFn) {
      return;
    }
    const testCtrl = new FormControl(sentinel);
    const errors = validatorFn(testCtrl) || {};
    return (errors as any)[type]?.[type];
  }

  protected readonly Object = Object;

  onFileSelect(event: FileSelectEvent) {
    if (event.files.length > 0) {
      this.form.get(this.fieldName())?.setValue(event.files[0]);
      this.form.get(this.fieldName())?.markAsTouched();
    }
  }
}
